/**
 * Shared Chart Utilities
 *
 * Common utilities for dashboard chart components to reduce duplication
 * and ensure consistent behavior across all charts.
 */

/**
 * Generate unique gradient IDs for chart components.
 * This prevents conflicts when multiple charts with gradients are rendered
 * on the same page. Each chart instance should call this with a unique prefix.
 *
 * @param prefix - A unique prefix for this chart instance (use React's useId)
 * @param names - Array of gradient names (e.g., ['fill', 'stroke'])
 * @returns Object with gradient IDs keyed by name
 */
export function createGradientIds<T extends string>(
  prefix: string,
  names: T[],
): Record<T, string> {
  return names.reduce(
    (acc, name) => {
      // Clean the prefix to be a valid SVG ID (remove colons from useId)
      const cleanPrefix = prefix.replace(/:/g, '');
      acc[name] = `${cleanPrefix}-${name}`;
      return acc;
    },
    {} as Record<T, string>,
  );
}

/**
 * Configuration for chart dot display based on data point count.
 * Shows dots for sparse data for better visibility, hides them for dense data.
 */
export interface DotConfig {
  /** Whether to show dots on data points */
  showDots: boolean;
  /** Radius of the dots (when shown) */
  dotRadius: number;
  /** Radius of active (hover) dots */
  activeDotRadius: number;
}

/**
 * Get dot configuration based on the number of data points.
 * Dots are shown when data is sparse for better visibility,
 * and hidden when data is dense to avoid visual clutter.
 *
 * @param dataLength - Number of data points in the chart
 * @returns Dot configuration object
 */
export function getChartDotConfig(dataLength: number): DotConfig {
  const showDots = dataLength <= 20;
  const dotRadius = dataLength <= 10 ? 4 : 3;

  return {
    showDots,
    dotRadius,
    activeDotRadius: dotRadius + 2,
  };
}

/**
 * Create dot props for Recharts Area/Line components.
 * Returns false when dots should be hidden, or dot configuration object.
 *
 * @param dataLength - Number of data points
 * @param colorVar - CSS variable for the dot color (e.g., 'var(--color-errors)')
 * @returns Dot props for Recharts
 */
export function createDotProps(
  dataLength: number,
  colorVar: string,
): false | { r: number; fill: string; strokeWidth: number } {
  const config = getChartDotConfig(dataLength);

  if (!config.showDots) {
    return false;
  }

  return {
    r: config.dotRadius,
    fill: colorVar,
    strokeWidth: 0,
  };
}

/**
 * Create active dot props for Recharts Area/Line components.
 * Active dots are shown on hover regardless of data density.
 *
 * @param dataLength - Number of data points
 * @param colorVar - CSS variable for the dot color
 * @returns Active dot props for Recharts
 */
export function createActiveDotProps(
  dataLength: number,
  colorVar: string,
): { r: number; fill: string; strokeWidth: number; stroke: string } {
  const config = getChartDotConfig(dataLength);

  return {
    r: config.activeDotRadius,
    fill: colorVar,
    strokeWidth: 2,
    stroke: 'hsl(var(--background))',
  };
}

/**
 * Determine loading/empty state for charts.
 * Standardizes the pattern used across all chart components.
 *
 * @param isLoading - Whether data is currently loading
 * @param data - The data array (or undefined if not yet loaded)
 * @returns Object with showSkeleton and showNoData flags
 */
export function getChartLoadingState<T>(
  isLoading: boolean,
  data: T[] | undefined,
): { showSkeleton: boolean; showNoData: boolean } {
  const showSkeleton = isLoading || data === undefined;
  const showNoData = !showSkeleton && data.length === 0;

  return { showSkeleton, showNoData };
}

/**
 * Extended loading state check that also validates data has values.
 * Useful for charts where empty arrays are valid but contain all zeros.
 *
 * @param isLoading - Whether data is currently loading
 * @param data - The data array
 * @param hasValues - Function to check if data has meaningful values
 * @returns Object with showSkeleton and showNoData flags
 */
export function getChartLoadingStateWithValues<T>(
  isLoading: boolean,
  data: T[] | undefined,
  hasValues: (data: T[]) => boolean,
): { showSkeleton: boolean; showNoData: boolean } {
  const showSkeleton = isLoading || data === undefined;
  const showNoData = !showSkeleton && (data.length === 0 || !hasValues(data));

  return { showSkeleton, showNoData };
}
